'use client';

import { c as _c } from "react/compiler-runtime";
import { BANK_FUNDING_METHODS, configuredFundingMethods, type FundingMethod, type FundingSavedCard, isBankFundingMethod, isHostedFundingMethod, onrampManifest, type UnavailableBankFundingMethod, type UnavailableFundingMethod } from '@morfi/onramp-sdk';
import { useTranslations } from 'next-intl';
import { type ChangeEvent, type MouseEvent, type ReactNode, useState } from 'react';
import { AppTabs } from '@/components/ui/app-tabs';
import { Icon } from '@/components/ui/icon';
import { Search01Icon } from '@/components/ui/icon/glyphs';
import { InputField } from '@/components/ui/input-field';
import { MotionAnimatedAutoHeight } from '@/components/ui/motion-animated-auto-height';
import { FundingLoading } from '../shared/funding-loading';
import { FundingBankMethods } from './funding-bank-methods';
import { FundingMethodRow } from './funding-method-row';
import { FundingSavedCardRow } from './funding-saved-card-row';
import { FundingWalletMethods } from './funding-wallet-methods';
export interface FundingMethodPickerViewProps {
  methods: readonly FundingMethod[];
  unavailableBankMethods?: readonly UnavailableBankFundingMethod[] | undefined;
  unavailableMethods?: readonly UnavailableFundingMethod[] | undefined;
  loading?: boolean;
  lowKycEligible?: boolean;
  directPurchase?: boolean;
  onSelect?: ((method: FundingMethod) => void) | undefined;
  onExchange?: (() => void) | undefined;
  onProfile?: (() => void) | undefined;
  providerFeedback?: ReactNode;
  providerFeedbackOwnsStage?: boolean;
  feedbackAfterMethods?: boolean;
  savedCards?: readonly FundingSavedCard[];
  busy?: boolean;
  onSelectCard?(id: string): void;
  onRemoveCard?(id: string): void;
  savedMethods: readonly FundingMethod[];
  tab: 'money' | 'wallets';
  setPickerCategory(value: 'money' | 'wallets'): void;
  connectedWalletEnabled: boolean;
  connectedWalletLoading: boolean;
  cryptoDepositEnabled: boolean;
  manualEnabled: boolean;
  exchangeEnabled: boolean;
  onConnectedWallet(): void;
  onCryptoDeposit(): void;
  onManualTransfer(): void;
}
export function FundingMethodPickerView(t0) {
  const $ = _c(101);
  const {
    methods,
    unavailableBankMethods: t1,
    unavailableMethods: t2,
    loading: t3,
    lowKycEligible: t4,
    directPurchase: t5,
    onSelect,
    onExchange,
    providerFeedback,
    feedbackAfterMethods: t6,
    savedCards: t7,
    busy: t8,
    onSelectCard,
    savedMethods: previousMethods,
    tab,
    setPickerCategory,
    connectedWalletEnabled,
    connectedWalletLoading,
    cryptoDepositEnabled,
    manualEnabled,
    exchangeEnabled,
    onConnectedWallet,
    onCryptoDeposit,
    onManualTransfer
  } = t0;
  const unavailableBankMethods = t1 === undefined ? [] : t1;
  const unavailableMethods = t2 === undefined ? [] : t2;
  const loading = t3 === undefined ? false : t3;
  const lowKycEligible = t4 === undefined ? false : t4;
  const directPurchase = t5 === undefined ? false : t5;
  const feedbackAfterMethods = t6 === undefined ? false : t6;
  let t9;
  if ($[0] !== t7) {
    t9 = t7 === undefined ? [] : t7;
    $[0] = t7;
    $[1] = t9;
  } else {
    t9 = $[1];
  }
  const savedCards = t9;
  const busy = t8 === undefined ? false : t8;
  const t = useTranslations("onramp");
  const [search, setSearch] = useState("");
  const query = search.trim().toLocaleLowerCase();
  const matches = method => t(method).toLocaleLowerCase().includes(query);
  let t10;
  if ($[2] !== methods) {
    t10 = new Set(methods);
    $[2] = methods;
    $[3] = t10;
  } else {
    t10 = $[3];
  }
  const selectableMethods = t10;
  let t11;
  if ($[4] !== directPurchase || $[5] !== selectableMethods) {
    t11 = method_0 => method_0 !== "card" && (!directPurchase || selectableMethods.has(method_0));
    $[4] = directPurchase;
    $[5] = selectableMethods;
    $[6] = t11;
  } else {
    t11 = $[6];
  }
  let t12;
  if ($[7] !== selectableMethods) {
    t12 = (left, right) => Number(selectableMethods.has(right)) - Number(selectableMethods.has(left));
    $[7] = selectableMethods;
    $[8] = t12;
  } else {
    t12 = $[8];
  }
  const savedMethods = previousMethods.filter(t11).sort(t12);
  const unavailableBankReasons = new Map(unavailableBankMethods.map(_temp));
  const availableMethods = methods.filter(method_1 => !isBankFundingMethod(method_1) && matches(method_1));
  const configuredMethods = configuredFundingMethods(onrampManifest, directPurchase);
  const disabledMethods = configuredMethods.filter(method_2 => !isBankFundingMethod(method_2) && !selectableMethods.has(method_2) && matches(method_2));
  const methodReasons = new Map(unavailableMethods.map(_temp2));
  let t13;
  if ($[9] !== t) {
    t13 = t("bank_unavailable.provider_unavailable");
    $[9] = t;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] !== t) {
    t14 = t("bank_unavailable.currency_unavailable");
    $[11] = t;
    $[12] = t14;
  } else {
    t14 = $[12];
  }
  let t15;
  if ($[13] !== t) {
    t15 = t("bank_unavailable.execution_unavailable");
    $[13] = t;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== t) {
    t16 = t("method_unavailable");
    $[15] = t;
    $[16] = t16;
  } else {
    t16 = $[16];
  }
  let t17;
  if ($[17] !== t13 || $[18] !== t14 || $[19] !== t15 || $[20] !== t16) {
    t17 = {
      provider_unavailable: t13,
      currency_unavailable: t14,
      execution_unavailable: t15,
      device_unavailable: t16
    };
    $[17] = t13;
    $[18] = t14;
    $[19] = t15;
    $[20] = t16;
    $[21] = t17;
  } else {
    t17 = $[21];
  }
  const unavailableCopy = t17;
  let t18;
  if ($[22] !== selectableMethods) {
    t18 = (left_0, right_0) => Number(selectableMethods.has(right_0)) - Number(selectableMethods.has(left_0));
    $[22] = selectableMethods;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  const bankMethods = BANK_FUNDING_METHODS.filter(method_3 => configuredMethods.includes(method_3) && (matches(method_3) || t("bank_transfers").toLocaleLowerCase().includes(query))).sort(t18);
  const showBankTransfers = !directPurchase && bankMethods.length > 0;
  const t19 = "flex flex-col gap-4";
  let t20;
  if ($[24] !== busy || $[25] !== onSelectCard || $[26] !== savedCards || $[27] !== t) {
    t20 = savedCards.length ? <section className="flex flex-col gap-2" aria-label={t("saved_cards")}><h3 className="text-micro font-medium uppercase tracking-wide text-foreground-secondary">{t("saved_cards")}</h3><div className="overflow-hidden rounded-control bg-background-tertiary">{savedCards.map(card => <FundingSavedCardRow key={card.id} card={card} busy={busy} onSelect={id => onSelectCard?.(id)} />)}</div></section> : null;
    $[24] = busy;
    $[25] = onSelectCard;
    $[26] = savedCards;
    $[27] = t;
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const t21 = savedMethods.length > 0 ? <section className="flex flex-col gap-2" aria-label={t("saved")}><h3 className="text-micro font-medium uppercase tracking-wide text-foreground-secondary">{t("saved")}</h3><div className="overflow-hidden rounded-control bg-background-tertiary">{savedMethods.map(method_4 => <FundingMethodRow key={method_4} method={method_4} lowKyc={lowKycEligible && selectableMethods.has(method_4)} subtitle={selectableMethods.has(method_4) ? t("previously_used") : t("method_unavailable")} onSelect={selectableMethods.has(method_4) ? onSelect : undefined} />)}</div></section> : null;
  const t22 = "flex flex-col gap-3";
  let t23;
  if ($[29] !== t) {
    t23 = t("more_options");
    $[29] = t;
    $[30] = t23;
  } else {
    t23 = $[30];
  }
  let t24;
  if ($[31] !== t) {
    t24 = t("more_options");
    $[31] = t;
    $[32] = t24;
  } else {
    t24 = $[32];
  }
  let t25;
  if ($[33] !== t24) {
    t25 = <h3 className="text-micro font-medium uppercase tracking-wide text-foreground-secondary">{t24}</h3>;
    $[33] = t24;
    $[34] = t25;
  } else {
    t25 = $[34];
  }
  const t26 = AppTabs;
  const t27 = "gap-0";
  const t28 = "manual";
  let t29;
  if ($[35] !== setPickerCategory) {
    t29 = value => setPickerCategory(value === "wallets" ? "wallets" : "money");
    $[35] = setPickerCategory;
    $[36] = t29;
  } else {
    t29 = $[36];
  }
  let t30;
  if ($[37] !== directPurchase || $[38] !== setPickerCategory || $[39] !== t) {
    t30 = !directPurchase && <AppTabs.List variant="segmented" size="md"><AppTabs.Trigger value="money" onMouseDown={deferPointerActivation} onClick={() => setPickerCategory("money")}>{t("add_money")}</AppTabs.Trigger><AppTabs.Trigger value="wallets" onMouseDown={deferPointerActivation} onClick={() => setPickerCategory("wallets")}>{t("wallets_and_exchanges")}</AppTabs.Trigger></AppTabs.List>;
    $[37] = directPurchase;
    $[38] = setPickerCategory;
    $[39] = t;
    $[40] = t30;
  } else {
    t30 = $[40];
  }
  const T0 = MotionAnimatedAutoHeight;
  const t31 = "flow-root";
  let t32;
  if ($[41] !== tab) {
    t32 = [tab];
    $[41] = tab;
    $[42] = t32;
  } else {
    t32 = $[42];
  }
  const t33 = AppTabs;
  const t34 = "money";
  const t35 = "mt-3 flex flex-col gap-3";
  const t36 = feedbackAfterMethods ? null : providerFeedback;
  let t37;
  if ($[43] !== t) {
    t37 = t("search_methods");
    $[43] = t;
    $[44] = t37;
  } else {
    t37 = $[44];
  }
  let t38;
  if ($[45] !== t) {
    t38 = t("search_methods_placeholder");
    $[45] = t;
    $[46] = t38;
  } else {
    t38 = $[46];
  }
  let t39;
  let t40;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = event => setSearch(event.target.value);
    t40 = <Icon icon={Search01Icon} className="size-icon-md text-foreground-secondary" />;
    $[47] = t39;
    $[48] = t40;
  } else {
    t39 = $[47];
    t40 = $[48];
  }
  let t41;
  if ($[49] !== loading || $[50] !== search || $[51] !== t37 || $[52] !== t38) {
    t41 = <InputField type="search" aria-label={t37} placeholder={t38} value={search} disabled={loading} onChange={t39} leftIcon={t40} className="bg-background-tertiary dark:bg-background-tertiary" />;
    $[49] = loading;
    $[50] = search;
    $[51] = t37;
    $[52] = t38;
    $[53] = t41;
  } else {
    t41 = $[53];
  }
  let t42;
  if ($[54] !== directPurchase || $[55] !== loading) {
    t42 = loading ? <FundingLoading banks={!directPurchase} /> : null;
    $[54] = directPurchase;
    $[55] = loading;
    $[56] = t42;
  } else {
    t42 = $[56];
  }
  const t43 = !loading && (availableMethods.length > 0 || disabledMethods.length > 0) ? <div className="overflow-hidden rounded-control bg-background-tertiary">{availableMethods.map(method_5 => <FundingMethodRow key={method_5} method={method_5} lowKyc={lowKycEligible} subtitle={method_5 === "moonpay_balance" ? t("moonpay_balance_subtitle") : isHostedFundingMethod(method_5) ? t("hosted_method_subtitle") : t("instant")} onSelect={onSelect} />)}{disabledMethods.map(method_6 => <FundingMethodRow key={method_6} method={method_6} subtitle={unavailableCopy[methodReasons.get(method_6) ?? "provider_unavailable"]} />)}</div> : null;
  const t44 = !loading && showBankTransfers ? <FundingBankMethods methods={bankMethods} selectableMethods={selectableMethods} unavailableReasons={unavailableBankReasons} onSelect={onSelect} /> : null;
  let t45;
  if ($[57] !== availableMethods || $[58] !== disabledMethods || $[59] !== loading || $[60] !== showBankTransfers || $[61] !== t) {
    t45 = !loading && availableMethods.length === 0 && disabledMethods.length === 0 && !showBankTransfers ? <p className="text-body text-foreground-secondary">{t("no_matching_methods")}</p> : null;
    $[57] = availableMethods;
    $[58] = disabledMethods;
    $[59] = loading;
    $[60] = showBankTransfers;
    $[61] = t;
    $[62] = t45;
  } else {
    t45 = $[62];
  }
  const t46 = feedbackAfterMethods ? providerFeedback : null;
  let t47;
  if ($[63] !== t33.Content || $[64] !== t36 || $[65] !== t41 || $[66] !== t42 || $[67] !== t43 || $[68] !== t44 || $[69] !== t45 || $[70] !== t46) {
    t47 = <t33.Content value={t34} className={t35}>{t36}{t41}{t42}{t43}{t44}{t45}{t46}</t33.Content>;
    $[63] = t33.Content;
    $[64] = t36;
    $[65] = t41;
    $[66] = t42;
    $[67] = t43;
    $[68] = t44;
    $[69] = t45;
    $[70] = t46;
    $[71] = t47;
  } else {
    t47 = $[71];
  }
  let t48;
  if ($[72] !== connectedWalletEnabled || $[73] !== connectedWalletLoading || $[74] !== cryptoDepositEnabled || $[75] !== exchangeEnabled || $[76] !== manualEnabled || $[77] !== onConnectedWallet || $[78] !== onCryptoDeposit || $[79] !== onExchange || $[80] !== onManualTransfer) {
    t48 = <AppTabs.Content value="wallets" className="mt-3 flex flex-col gap-3"><FundingWalletMethods connectedWalletEnabled={connectedWalletEnabled} connectedWalletLoading={connectedWalletLoading} onConnectedWallet={onConnectedWallet} cryptoDepositEnabled={cryptoDepositEnabled} manualEnabled={manualEnabled} exchangeEnabled={exchangeEnabled} onCryptoDeposit={onCryptoDeposit} onManualTransfer={onManualTransfer} onExchange={onExchange} /></AppTabs.Content>;
    $[72] = connectedWalletEnabled;
    $[73] = connectedWalletLoading;
    $[74] = cryptoDepositEnabled;
    $[75] = exchangeEnabled;
    $[76] = manualEnabled;
    $[77] = onConnectedWallet;
    $[78] = onCryptoDeposit;
    $[79] = onExchange;
    $[80] = onManualTransfer;
    $[81] = t48;
  } else {
    t48 = $[81];
  }
  let t49;
  if ($[82] !== T0 || $[83] !== t32 || $[84] !== t47 || $[85] !== t48) {
    t49 = <T0 innerClassName={t31} layoutDependencies={t32}>{t47}{t48}</T0>;
    $[82] = T0;
    $[83] = t32;
    $[84] = t47;
    $[85] = t48;
    $[86] = t49;
  } else {
    t49 = $[86];
  }
  let t50;
  if ($[87] !== t26.Root || $[88] !== t29 || $[89] !== t30 || $[90] !== t49 || $[91] !== tab) {
    t50 = <t26.Root className={t27} value={tab} activationMode={t28} onValueChange={t29}>{t30}{t49}</t26.Root>;
    $[87] = t26.Root;
    $[88] = t29;
    $[89] = t30;
    $[90] = t49;
    $[91] = tab;
    $[92] = t50;
  } else {
    t50 = $[92];
  }
  let t51;
  if ($[93] !== t23 || $[94] !== t25 || $[95] !== t50) {
    t51 = <section className={t22} aria-label={t23}>{t25}{t50}</section>;
    $[93] = t23;
    $[94] = t25;
    $[95] = t50;
    $[96] = t51;
  } else {
    t51 = $[96];
  }
  let t52;
  if ($[97] !== t20 || $[98] !== t21 || $[99] !== t51) {
    t52 = <div className={t19}>{t20}{t21}{t51}</div>;
    $[97] = t20;
    $[98] = t21;
    $[99] = t51;
    $[100] = t52;
  } else {
    t52 = $[100];
  }
  return t52;
}
function _temp2(entry_0) {
  return [entry_0.method, entry_0.reason];
}
function _temp(entry) {
  return [entry.method, entry.reason];
}
function deferPointerActivation(event: MouseEvent<HTMLButtonElement>): void {
  // Selecting on mouse-down recenters the shorter sheet before mouse-up, turning the click into a backdrop dismissal.
  event.preventDefault();
}